import type React from "react";
import { useState } from "react";
import cn from "classnames";
import { Search, X } from "lucide-react";
import { Input } from "~/atoms/input";
import { useDebounce } from "~/api/utils";

interface SearchBarProps {
  placeholder?: string;
  value?: string;
  onSearch: (searchValue: string) => void;
  onClearSearch: () => void;
  onDebouncedSearch?: (searchValue: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  onClearSearch,
  onDebouncedSearch,
  value = "",
  placeholder,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setInputValue(value);
  }
  const debouncedSearch = useDebounce((value: string) => {
    if (onDebouncedSearch) onDebouncedSearch(value);
  }, 500);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearch(inputValue);
    }
  };

  const handleClearSearch = () => {
    onClearSearch();
    setInputValue("");
    debouncedSearch("");
  };

  return (
    <div className="relative w-full max-w-80">
      <div className="pointer-events-none absolute inset-y-0 inset-s-0 flex items-center ps-3.5">
        <Search className="text-muted-foreground size-5" />
      </div>
      <Input
        name="search"
        placeholder={placeholder}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setInputValue(e.target.value);
          debouncedSearch(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        value={inputValue}
        className={cn("ps-10", { "pe-10": inputValue })}
      />
      {inputValue && (
        <button className="absolute inset-y-0 inset-e-0 flex items-center pe-3">
          <X
            className="text-muted-foreground size-5"
            onClick={handleClearSearch}
          />
        </button>
      )}
    </div>
  );
};

export default SearchBar;
